// ─── 색상 유틸리티 ───────────────────────────────────────────────────────────

export const DEFAULT_THEME = {
	headerStart: '#083F87',
	headerEnd: '#0D2848',
	sidebarStart: '#0C3A76',
	sidebarEnd: '#0A223D',
	headerText: '#ffffff',
	sidebarText: '#ffffff',
} as const;

const LEGACY_THEME_STORAGE_KEYS = [
	'themeHeaderStart',
	'themeHeaderEnd',
	'themeSidebarStart',
	'themeSidebarEnd',
	'themeHeaderTextColor',
	'themeSidebarTextColor',
] as const;

function clearLegacyThemeLocalStorage() {
	if (typeof localStorage === 'undefined') return;
	for (const key of LEGACY_THEME_STORAGE_KEYS) {
		localStorage.removeItem(key);
	}
}

function hexToRgb(hex: string) {
	const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
	return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : null;
}

/** ratio 0~1: 1이면 흰색, 0이면 원래 색 */
function lightenHex(hex: string, ratio: number): string {
	const c = hexToRgb(hex);
	if (!c) return hex;
	const r = Math.round(c.r + (255 - c.r) * ratio);
	const g = Math.round(c.g + (255 - c.g) * ratio);
	const b = Math.round(c.b + (255 - c.b) * ratio);
	return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

function hslToHex(h: number, s: number, l: number): string {
	s /= 100; l /= 100;
	const a = s * Math.min(l, 1 - l);
	const f = (n: number) => {
		const k = (n + h / 30) % 12;
		const v = l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
		return Math.round(v * 255).toString(16).padStart(2, '0');
	};
	return `#${f(0)}${f(8)}${f(4)}`;
}

/** hex 색의 hue를 추출해 선명하고 밝은 강조 색 반환 */
function getVividAccent(hex: string): string {
	const c = hexToRgb(hex);
	if (!c) return '#0785f3';
	const r = c.r / 255, g = c.g / 255, b = c.b / 255;
	const max = Math.max(r, g, b), min = Math.min(r, g, b);
	let h = 0;
	if (max !== min) {
		const d = max - min;
		switch (max) {
			case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
			case g: h = ((b - r) / d + 2) / 6; break;
			case b: h = ((r - g) / d + 4) / 6; break;
		}
	}
	return hslToHex(Math.round(h * 360), 78, 60);
}

function hasColorPair(value: string | null | undefined): boolean {
	return typeof value === 'string' && value.trim() !== '';
}

export const useLayoutStore = defineStore("layoutStore", {
	state: () => {
		clearLegacyThemeLocalStorage();
		return {
			layoutName: "mobile",
			isLoading: false,
			openSidebar: false,
			themeHeaderStart: DEFAULT_THEME.headerStart,
			themeHeaderEnd: DEFAULT_THEME.headerEnd,
			themeSidebarStart: DEFAULT_THEME.sidebarStart,
			themeSidebarEnd: DEFAULT_THEME.sidebarEnd,
			themeHeaderTextColor: DEFAULT_THEME.headerText,
			themeSidebarTextColor: DEFAULT_THEME.sidebarText,
		};
	},

	actions: {
		setLayout(layout: string) {
			this.layoutName = layout;
		},
		setSidebar(value: boolean) {
			this.openSidebar = value;
		},
		setWrapperLayout() {
			if (this.openSidebar === true) {
				let wrapper = document.getElementsByClassName("wrapper")[0];
				let teb_depthmenu = document.getElementsByClassName("teb_depthmenu")[0];
				wrapper.classList.add("sideon");
				teb_depthmenu.classList.add("sideon");
			}
		},
		setThemeColors(headerStart: string, headerEnd: string, sidebarStart: string, sidebarEnd: string, headerTextColor?: string, sidebarTextColor?: string) {
			this.themeHeaderStart  = headerStart;
			this.themeHeaderEnd    = headerEnd;
			this.themeSidebarStart = sidebarStart;
			this.themeSidebarEnd   = sidebarEnd;
			if (headerTextColor)  this.themeHeaderTextColor  = headerTextColor;
			if (sidebarTextColor) this.themeSidebarTextColor = sidebarTextColor;
			this.applyThemeColors();
		},
		resetThemeToDefaults() {
			this.themeHeaderStart = DEFAULT_THEME.headerStart;
			this.themeHeaderEnd = DEFAULT_THEME.headerEnd;
			this.themeSidebarStart = DEFAULT_THEME.sidebarStart;
			this.themeSidebarEnd = DEFAULT_THEME.sidebarEnd;
			this.themeHeaderTextColor = DEFAULT_THEME.headerText;
			this.themeSidebarTextColor = DEFAULT_THEME.sidebarText;
			this.applyThemeColors();
		},
		// DB에서 불러온 "start,end" 형식 문자열을 파싱해 CSS 변수로 적용 (없으면 기본값)
		syncFromUserInfo(headerColor: string | null | undefined, sidebarColor: string | null | undefined, headerTextColor?: string | null, sidebarTextColor?: string | null) {
			if (hasColorPair(headerColor)) {
				const [s, e] = headerColor!.split(',');
				this.themeHeaderStart = s?.trim() || DEFAULT_THEME.headerStart;
				this.themeHeaderEnd = e?.trim() || DEFAULT_THEME.headerEnd;
			} else {
				this.themeHeaderStart = DEFAULT_THEME.headerStart;
				this.themeHeaderEnd = DEFAULT_THEME.headerEnd;
			}

			if (hasColorPair(sidebarColor)) {
				const [s, e] = sidebarColor!.split(',');
				this.themeSidebarStart = s?.trim() || DEFAULT_THEME.sidebarStart;
				this.themeSidebarEnd = e?.trim() || DEFAULT_THEME.sidebarEnd;
			} else {
				this.themeSidebarStart = DEFAULT_THEME.sidebarStart;
				this.themeSidebarEnd = DEFAULT_THEME.sidebarEnd;
			}

			this.themeHeaderTextColor = headerTextColor?.trim() || DEFAULT_THEME.headerText;
			this.themeSidebarTextColor = sidebarTextColor?.trim() || DEFAULT_THEME.sidebarText;
			this.applyThemeColors();
		},
		applyThemeColors() {
			if (typeof document === 'undefined') return;
			const el = document.documentElement;

			// 헤더/사이드바 그라디언트
			el.style.setProperty('--main-header-background-color-start',  this.themeHeaderStart);
			el.style.setProperty('--main-header-background-color-end',    this.themeHeaderEnd);
			el.style.setProperty('--main-sidebar-background-color-start', this.themeSidebarStart);
			el.style.setProperty('--main-sidebar-background-color-end',   this.themeSidebarEnd);

			// 사이드바 선택/호버 파생 색상
			const rgb = hexToRgb(this.themeSidebarStart);
			const shadow = rgb
				? `0 2px 2px 1px rgba(${rgb.r},${rgb.g},${rgb.b},0.42), 0 3px 3px 1px rgba(${rgb.r},${rgb.g},${rgb.b},0.28)`
				: '0 2px 2px 1px #0d27496c, 0 3px 3px 1px #0a2b5742';

			el.style.setProperty('--sidebar-active-bg',            lightenHex(this.themeSidebarStart, 0.88));
			el.style.setProperty('--sidebar-active-text-color',    this.themeSidebarStart);
			el.style.setProperty('--sidebar-active-shadow',        shadow);
			el.style.setProperty('--sidebar-hover-bg',             lightenHex(this.themeSidebarStart, 0.08));
			el.style.setProperty('--sidebar-hover-shadow',         shadow);
			el.style.setProperty('--sidebar-accent-color',         getVividAccent(this.themeSidebarStart));

			// 텍스트 색상
			el.style.setProperty('--main-header-text-color',  this.themeHeaderTextColor);
			el.style.setProperty('--main-sidebar-text-color', this.themeSidebarTextColor);
		},
	},
});
